import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);


// User Level Progress

export const getUserLevelProgresses = async () => {
    try {
        const response = await api.get('/progress/user-level-progresses/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserLevelProgress = async (progressData) => {
    try {
        const response = await api.post('/progress/user-level-progresses/', progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserLevelProgress = async (progressId, progressData) => {
    try {
        const response = await api.patch(`/progress/user-level-progresses/${progressId}/`, progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserLevelProgress = async (progressId) => {
    try {
        const response = await api.delete(`/progress/user-level-progresses/${progressId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// User Topic Exam Progress

// levelId is optional -- pass it to scope the response to just that level's topics/exams
// (what Level.jsx's sidebar needs) instead of the caller's entire platform-wide history.
export const getUserTopicExamProgresses = async (levelId) => {
    try {
        const response = await api.get('/progress/user-topic-exam-progresses/', {
            params: levelId ? { level: levelId } : undefined,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserTopicExamProgress = async (progressData) => {
    try {
        const response = await api.post('/progress/user-topic-exam-progresses/', progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserTopicExamProgress = async (progressId, progressData) => {
    try {
        const response = await api.patch(`/progress/user-topic-exam-progresses/${progressId}/`, progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserTopicExamProgress = async (progressId) => {
    try {
        const response = await api.delete(`/progress/user-topic-exam-progresses/${progressId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// User Topic Content Progress

// levelId is optional -- see getUserTopicExamProgresses above for why.
export const getUserTopicContentProgresses = async (levelId) => {
    try {
        const response = await api.get('/progress/user-topic-content-progresses/', {
            params: levelId ? { level: levelId } : undefined,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserTopicContentProgress = async (progressData) => {
    try {
        const response = await api.post('/progress/user-topic-content-progresses/', progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserTopicContentProgress = async (progressId, progressData) => {
    try {
        const response = await api.patch(`/progress/user-topic-content-progresses/${progressId}/`, progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserTopicContentProgress = async (progressId) => {
    try {
        const response = await api.delete(`/progress/user-topic-content-progresses/${progressId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// User Lesson Progress

export const getUserLessonProgresses = async () => {
    try {
        const response = await api.get('/progress/user-lesson-progresses/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserLessonProgress = async (progressData) => {
    try {
        const response = await api.post('/progress/user-lesson-progresses/', progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserLessonProgress = async (progressId, progressData) => {
    try {
        const response = await api.patch(`/progress/user-lesson-progresses/${progressId}/`, progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserLessonProgress = async (progressId) => {
    try {
        const response = await api.delete(`/progress/user-lesson-progresses/${progressId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// User Lesson Video Progress

export const getUserLessonVideoProgresses = async () => {
    try {
        const response = await api.get('/progress/user-lesson-video-progresses/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserLessonVideoProgress = async (progressData) => {
    try {
        const response = await api.post('/progress/user-lesson-video-progresses/', progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserLessonVideoProgress = async (progressId, progressData) => {
    try {
        const response = await api.patch(`/progress/user-lesson-video-progresses/${progressId}/`, progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserLessonVideoProgress = async (progressId) => {
    try {
        const response = await api.delete(`/progress/user-lesson-video-progresses/${progressId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}